"use client";

// BentoServices — Signal rebrand.
//
// Four service cards, each with a service-specific SVG schematic instead
// of a colored icon square. All four diagrams are drawn at the same
// stroke width (1.1px) and viewport (150×42) so they read as one family.
//
// Bento arrangement (per user direction, not the moodboard's 4-cell layout):
//   Row 1: IP Transit (wide)      DWDM Wavelengths (wide)
//   Row 2: Ethernet (standard)    Dark Fibre (narrow)
//
// Rationale: IP Transit and DWDM are the volume business, available at all
// 18 PoPs. Ethernet is a standard cell. Dark Fibre is narrowest, scope
// limited to the Sofia metro. Below 640px the four cards stack; the
// wide/narrow distinction is irrelevant on mobile.
//
// Spec values read from the same sources every other surface uses:
//   - SLA percentages: network-metrics.ts (IP_TRANSIT_SLA_PERCENT,
//     DWDM_SLA_PERCENT, ETHERNET_L2VPN_SLA_PERCENT)
//   - Lead-time ranges: data/provisioning-lead-times.ts (leadTimeFor)
//   - Capacity ranges: literal strings for now, mirroring what Tech
//     Specs tables show. A follow-up would put them in a single source,
//     flagged in the rebrand report.

import { memo, type ReactNode } from "react";
import { motion, type Variants } from "framer-motion";
import Link from "@/components/LocalizedLink";
import { useTranslation } from "@/lib/i18n";
import {
  IP_TRANSIT_SLA_PERCENT,
  DWDM_SLA_PERCENT,
  ETHERNET_L2VPN_SLA_PERCENT,
} from "@/lib/network-metrics";
import { leadTimeFor } from "@/data/provisioning-lead-times";

/**
 * Compact spec-row lead-time renderer.
 *
 * Renders "5–10 d" / "15–30 d" — the compact spec value used inside
 * schematic cards. Uses the site-wide spec_days_short i18n key so BG
 * gets "дн" instead of "d".
 */
function useShortLead(service: "ip-transit" | "ethernet-l2vpn" | "dwdm" | "dark-fibre") {
  const { t } = useTranslation();
  const [min, max] = leadTimeFor(service).days;
  const value = min === max ? String(min) : `${min}\u2013${max}`;
  return `${value} ${t("spec_days_short")}`;
}

type SpecLabelKey =
  | "spec_capacity"
  | "spec_ports"
  | "spec_sla"
  | "spec_lead"
  | "spec_fibre"
  | "spec_scope";

type DescKey = "ip_transit_desc" | "l2vpn_desc" | "dwdm_desc" | "dark_fiber_desc";

type SpecRow = { labelKey: SpecLabelKey; value: string };

type ServiceDef = {
  key: "ip-transit" | "ethernet-l2vpn" | "dwdm" | "dark-fibre";
  title: string;
  descKey: DescKey;
  href: string;
  schematic: ReactNode;
  specs: SpecRow[];
  span: string;
};

type TFn = ReturnType<typeof useTranslation>["t"];

// SVGs — all 150×42, stroke-width 1.1 on the primary path, accents in
// var(--accent) where the diagram wants a single highlighted point.
// currentColor is used for the "faint" dots so the schematic tracks the
// theme without inline dark/light branching.

const svgWavelengths = (
  <svg
    width="150"
    height="42"
    viewBox="0 0 150 42"
    aria-hidden="true"
    className="text-[color:var(--signal-ink-dim)]"
  >
    {/* Sine wave — capacity as continuous spectrum. */}
    <path
      d="M0 21 C 12 4, 25 38, 37 21 S 62 4, 75 21 S 100 38, 112 21 S 137 4, 150 21"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.1"
    />
    <circle cx="37" cy="21" r="3" fill="var(--accent)" />
    <circle cx="112" cy="21" r="3" fill="var(--accent)" />
  </svg>
);

const svgIpTransit = (
  <svg
    width="150"
    height="42"
    viewBox="0 0 150 42"
    aria-hidden="true"
    className="text-[color:var(--signal-ink-dim)]"
  >
    {/* Star topology — one central AS peering with many networks. */}
    <g stroke="currentColor" strokeWidth="1" fill="none">
      <path d="M75 21 L 20 8" />
      <path d="M75 21 L 14 26" />
      <path d="M75 21 L 34 38" />
      <path d="M75 21 L 130 10" />
      <path d="M75 21 L 138 30" />
      <path d="M75 21 L 108 39" />
    </g>
    <circle cx="75" cy="21" r="5" fill="var(--accent)" />
    <g fill="var(--signal-ink-faint)">
      <circle cx="20" cy="8" r="2.4" />
      <circle cx="14" cy="26" r="2.4" />
      <circle cx="34" cy="38" r="2.4" />
      <circle cx="130" cy="10" r="2.4" />
      <circle cx="138" cy="30" r="2.4" />
      <circle cx="108" cy="39" r="2.4" />
    </g>
  </svg>
);

const svgDarkFibre = (
  <svg
    width="150"
    height="42"
    viewBox="0 0 150 42"
    aria-hidden="true"
    className="text-[color:var(--signal-ink-dim)]"
  >
    {/* Point-to-point pair with terminating vertical strokes. */}
    <line x1="10" y1="21" x2="140" y2="21" stroke="currentColor" strokeWidth="1.1" />
    <line x1="10" y1="17" x2="10" y2="25" stroke="var(--accent)" strokeWidth="2" />
    <line x1="140" y1="17" x2="140" y2="25" stroke="var(--accent)" strokeWidth="2" />
    <circle cx="75" cy="21" r="2.4" fill="var(--signal-ink-faint)" />
  </svg>
);

const svgEthernet = (
  <svg
    width="150"
    height="42"
    viewBox="0 0 150 42"
    aria-hidden="true"
    className="text-[color:var(--signal-ink-dim)]"
  >
    {/*
      Tagged-trunk motif — a horizontal trunk carrying three tagged VLAN
      strands that diverge into distinct endpoints on the right. Drawn at
      the same 1.1 stroke as the others so the family reads together.
    */}
    <line x1="10" y1="21" x2="70" y2="21" stroke="currentColor" strokeWidth="1.1" />
    {/* Split into three tagged streams */}
    <path d="M70 21 L 140 8" stroke="currentColor" strokeWidth="1" fill="none" />
    <path d="M70 21 L 140 21" stroke="currentColor" strokeWidth="1" fill="none" />
    <path d="M70 21 L 140 34" stroke="currentColor" strokeWidth="1" fill="none" />
    {/* Trunk anchor */}
    <circle cx="70" cy="21" r="3" fill="var(--accent)" />
    {/* Three tagged endpoints */}
    <g fill="var(--signal-ink-faint)">
      <circle cx="140" cy="8" r="2.4" />
      <circle cx="140" cy="21" r="2.4" />
      <circle cx="140" cy="34" r="2.4" />
    </g>
    {/* VLAN tag ticks along the trunk */}
    <g stroke="var(--signal-ink-faint)" strokeWidth="1">
      <line x1="25" y1="18" x2="25" y2="24" />
      <line x1="42" y1="18" x2="42" y2="24" />
      <line x1="59" y1="18" x2="59" y2="24" />
    </g>
  </svg>
);

const containerVariants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.1 },
  },
};

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } },
};

export const BentoServices = memo(function BentoServices() {
  const { t } = useTranslation();

  const ipTransitLead = useShortLead("ip-transit");
  const ethernetLead = useShortLead("ethernet-l2vpn");
  const dwdmLead = useShortLead("dwdm");
  const darkFibreLead = useShortLead("dark-fibre");

  const services: ServiceDef[] = [
    {
      key: "ip-transit",
      title: "IP Transit",
      descKey: "ip_transit_desc",
      href: "/services/ip-transit",
      schematic: svgIpTransit,
      specs: [
        { labelKey: "spec_ports", value: "1G\u2013400G" },
        { labelKey: "spec_sla", value: IP_TRANSIT_SLA_PERCENT },
        { labelKey: "spec_lead", value: ipTransitLead },
      ],
      // Row 1 wide (matches ROW 1 md:grid-cols-2).
      span: "md:col-span-1",
    },
    {
      key: "dwdm",
      title: "Wavelengths (DWDM)",
      descKey: "dwdm_desc",
      href: "/services/wavelengths-dwdm",
      schematic: svgWavelengths,
      specs: [
        { labelKey: "spec_capacity", value: "10G\u2013400G" },
        { labelKey: "spec_sla", value: DWDM_SLA_PERCENT },
        { labelKey: "spec_lead", value: dwdmLead },
      ],
      span: "md:col-span-1",
    },
    {
      key: "ethernet-l2vpn",
      title: "Ethernet / L2VPN",
      descKey: "l2vpn_desc",
      href: "/services/ethernet-l2vpn",
      schematic: svgEthernet,
      specs: [
        { labelKey: "spec_capacity", value: "10M\u201310G" },
        { labelKey: "spec_sla", value: ETHERNET_L2VPN_SLA_PERCENT },
        { labelKey: "spec_lead", value: ethernetLead },
      ],
      // Row 2 standard cell — 2fr of the 2fr/1fr split.
      span: "md:col-span-2",
    },
    {
      key: "dark-fibre",
      title: "Dark Fibre",
      descKey: "dark_fiber_desc",
      href: "/services/dark-fiber",
      schematic: svgDarkFibre,
      specs: [
        { labelKey: "spec_fibre", value: "G.652D" },
        { labelKey: "spec_scope", value: t("spec_scope_sofia_metro") },
        { labelKey: "spec_lead", value: darkFibreLead },
      ],
      // Row 2 narrow cell — 1fr of the 2fr/1fr split.
      span: "md:col-span-1",
    },
  ];

  // Split into two rows: [0,1] top (2fr/2fr), [2,3] bottom (2fr/1fr).
  const row1 = [services[0], services[1]];
  const row2 = [services[2], services[3]];

  return (
    <section id="services" className="py-24 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-7xl">
        {/* Section header — matched to the Signal treatment. */}
        <div className="text-center mb-16">
          <span
            className="font-mono text-[11px] sm:text-[12px] lowercase text-eyebrow"
            style={{ letterSpacing: "0.08em" }}
          >
            {t("bento_eyebrow")}
          </span>
          <h2
            className="font-display mt-3 text-3xl sm:text-4xl tracking-tight text-foreground"
            style={{ fontWeight: 600 }}
          >
            {t("bento_heading")}
          </h2>
          <p className="mt-4 max-w-xl mx-auto text-muted-foreground">
            {t("bento_subtitle")}
          </p>
        </div>

        {/*
          Bento — two rows of 2 cells each. Row 1 equal (2fr/2fr), row 2
          weighted (2fr/1fr). CSS Grid on md+ uses grid-cols-3 so the
          weighted row can express "2 of 3 columns" for Ethernet and
          "1 of 3 columns" for Dark Fibre.
        */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="flex flex-col gap-4"
        >
          {/* Row 1 — equal cells */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {row1.map((service) => (
              <SchematicCard key={service.key} service={service} tFn={t} />
            ))}
          </div>
          {/* Row 2 — weighted 2fr/1fr */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {row2.map((service) => (
              <SchematicCard key={service.key} service={service} tFn={t} />
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
});
BentoServices.displayName = "BentoServices";

/* ── Schematic card ──────────────────────────────────────────────────── */

function SchematicCard({
  service,
  tFn,
}: {
  service: ServiceDef;
  tFn: TFn;
}) {
  return (
    <motion.div
      variants={itemVariants}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className={service.span}
    >
      <Link href={service.href} className="block h-full">
        <div className="signal-sch-card h-full flex flex-col">
          {/* Schematic — 150×42 SVG, same viewport across all four. */}
          <div className="mb-4">{service.schematic}</div>

          {/* Title + arrow */}
          <div className="flex items-center gap-2 mb-2">
            <h3 className="text-lg font-semibold text-foreground">
              {service.title}
            </h3>
            <svg
              className="w-4 h-4 text-muted opacity-60"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth="2"
              aria-hidden="true"
            >
              <path strokeLinecap="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </div>

          {/* Description — min-h reserves 3 lines so descriptions of
              different lengths level off within a row. `flex-1` still pushes
              the spec row to the bottom in cards that exceed that reservation.
              Prevents the "gap above spec row" gap when Dark Fibre's 3-line
              description stretches the shared row height. */}
          <p className="text-sm text-muted-foreground leading-relaxed mb-4 flex-1 min-h-[4.5rem]">
            {tFn(service.descKey)}
          </p>

          {/* Spec row — mono, lowercase labels + tabular values. */}
          <div
            className="flex flex-wrap gap-x-4 gap-y-1 pt-3 border-t border-[var(--signal-line-soft)] font-mono text-[11px] text-[color:var(--signal-ink-faint)]"
            style={{ letterSpacing: "0.02em" }}
          >
            {service.specs.map((s) => (
              <span key={s.labelKey} className="whitespace-nowrap">
                <span className="lowercase">{tFn(s.labelKey)}</span>{" "}
                <b className="text-foreground font-medium tabular-nums not-italic normal-case">
                  {s.value}
                </b>
              </span>
            ))}
          </div>
        </div>
      </Link>
    </motion.div>
  );
}
